import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Paper, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OSTable from './OSTable';
import OSForm from './OSForm';
import DeleteOSDialog from './DeleteOSDialog';
import {
    fetchObrasSociales,
    createObraSocial,
    updateObraSocial,
    deleteObraSocial
} from '../../services/empleados.api';
import { useSnackbar } from 'notistack';
import Navbar from '../../../../../Navbar';
import Title from '../../../../../Title';
import { useDebounce } from '../../../../../../Hooks/UseDebounce';

const OSPage = () => {
    const [osData, setOsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [currentOS, setCurrentOS] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        nombre: '',
        empresa: '',
        plan: '',
        nro_os: ''
    });
    const { enqueueSnackbar } = useSnackbar();

    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        loadOS({ 
            page: page + 1, 
            rowsPerPage: rowsPerPage, 
            ...debouncedFilters 
        });
    }, [page, rowsPerPage, debouncedFilters]);

    const loadOS = async (params = {}) => {
        setLoading(true);
        try {
            const data = await fetchObrasSociales(params);
            if (data.data) {
                setOsData(data.data);
                setTotal(parseInt(data.total) || data.data.length);
            } else {
                setOsData(data || []);
                setTotal(data.length || 0);
            }
        } catch (error) {
            console.error("Error loading obras sociales:", error);
            enqueueSnackbar('Error al cargar obras sociales', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({ ...prev, [column]: value }));
    };

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters]);

    const handleOpenCreate = () => {
        setCurrentOS(null);
        setFormOpen(true);
    };

    const handleOpenEdit = (os) => {
        setCurrentOS(os);
        setFormOpen(true);
    };

    const handleOpenDelete = (os) => {
        setCurrentOS(os);
        setDeleteOpen(true);
    };

    const getUser = () => {
        return localStorage.getItem("user") || "unknown";
    };

    const handleFormSubmit = async (formData) => {
        try {
            const user = getUser();
            const payload = { ...formData, username: user };

            if (currentOS) {
                await updateObraSocial(currentOS.id_obra_social || currentOS.id, payload);
                enqueueSnackbar('Obra Social actualizada correctamente', { variant: 'success' });
            } else {
                await createObraSocial(payload);
                enqueueSnackbar('Obra Social creada correctamente', { variant: 'success' });
            }
            setFormOpen(false);
            loadOS({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error saving obra social:", error);
            enqueueSnackbar('Error al guardar la obra social', { variant: 'error' });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!currentOS) return;
        try {
            await deleteObraSocial(currentOS.id_obra_social || currentOS.id);
            enqueueSnackbar('Obra Social eliminada correctamente', { variant: 'success' });
            setDeleteOpen(false);
            loadOS({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error deleting obra social:", error);
            enqueueSnackbar('Error al eliminar la obra social', { variant: 'error' });
        }
    };

    return (
        <Container maxWidth="xl" sx={{  mb: 1 }}>
            <Navbar />
            <Title titulo="Gestión de Obras Sociales" handleClose={() => window.history.back()} />
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3} mb={3}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                        px: 3,
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                        }
                    }}
                >
                    Nueva Obra Social
                </Button>
            </Box>

            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <OSTable
                    data={osData}
                    loading={loading}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={total}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
            </Paper>

            <OSForm
                open={formOpen}
                handleClose={() => setFormOpen(false)}
                handleSubmit={handleFormSubmit}
                initialData={currentOS}
            />

            <DeleteOSDialog
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleDeleteConfirm}
                itemName={currentOS?.nombre}
            />
        </Container>
    );
};

export default OSPage;
